import { safeName } from "@/common/recordTree";
import {
  HGAnyRecordData,
  HGEra,
  HGIntersectionType,
  HGInterval,
  HGRecord,
  HGRecordsMap,
  HGTimeRecordPlus,
  HGTimeRecord,
} from "../common/types";
import { constructIntervalFromAPI } from "@/common/intervals";
import { constructIntervalForAPI } from "@/common/intervals";
import { intervalsAreEqual } from "@/common/intervals";
import { calculateIntersection } from "@/common/intersection";
import { store } from "@/state/redux/store";
import { hgApi } from "./api";

function eraFromString(era: string): HGEra | null {
  if (era.toLowerCase() == "bc") {
    return HGEra.BC;
  } else if (era.toLowerCase() == "ad") {
    return HGEra.AD;
  }
  return null;
}

function expandRecordsMap(recordsMap: HGRecordsMap): HGTimeRecord[] {
  const records: HGTimeRecord[] = [];
  for (const eraMap in recordsMap) {
    const era = eraFromString(eraMap);
    if (!era) continue;

    const constructRecord = (
      record: HGRecord,
      startSecond: string,
      endSecond: string | null,
      interval: HGInterval,
      endInterval: HGInterval | null,
      endEra: HGEra | null = null,
    ) => {
      const start_second =
        era == HGEra.BC ? -BigInt(startSecond) : BigInt(startSecond);
      let end_second: null | bigint = null;
      if (endSecond) {
        end_second =
          endEra == HGEra.BC ? -BigInt(endSecond) : BigInt(endSecond);
      }
      const end_interval = endInterval;
      const rec = {
        ...record,
        start_second,
        end_second,
        interval,
        end_interval,
      };
      if (rec.modified) {
        rec.modified = new Date(rec.modified).getTime();
      }
      if (rec.created) {
        rec.created = new Date(rec.created).getTime();
      }

      return rec;
    };

    const eraRecords = recordsMap[era];
    for (const it in eraRecords) {
      const itRecords = eraRecords[it];
      for (const n in itRecords) {
        const interval = constructIntervalFromAPI(n, it, era);
        const intervalRecords = itRecords[n];
        const oneIntervalRecords = intervalRecords["records"];
        const startSecond = intervalRecords["start_second"];
        const endSecond = intervalRecords["end_second"];
        if (oneIntervalRecords) {
          for (const mapRecord of oneIntervalRecords["finished"] || []) {
            records.push(
              constructRecord(
                mapRecord,
                startSecond,
                endSecond,
                interval,
                interval,
              ),
            );
          }
          for (const mapRecord of oneIntervalRecords["unfinished"] || []) {
            records.push(
              constructRecord(mapRecord, startSecond, null, interval, null),
            );
          }
        }
        const endEras = intervalRecords["end"];
        if (endEras) {
          for (const endEraMap in endEras) {
            const endEra = eraFromString(endEraMap);
            if (!endEra) continue;
            const endIts = endEras[endEra];
            for (const endIt in endIts) {
              const endNs = endIts[endIt];
              for (const endN in endNs) {
                const end_interval = constructIntervalFromAPI(
                  endN,
                  endIt,
                  endEra,
                );
                const endRecords = endNs[endN]["records"];
                const endSecond = endNs[endN]["end_second"];
                for (const mapRecord of endRecords["finished"]) {
                  records.push(
                    constructRecord(
                      mapRecord,
                      startSecond,
                      endSecond,
                      interval,
                      end_interval,
                      endEra,
                    ),
                  );
                }
              }
            }
          }
        }
      }
    }
  }
  return records;
}

async function apiRequest(
  url: string,
  method: string,
  body?: unknown,
  signal?: AbortSignal,
) {
  const params: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    signal,
  };
  if (body) {
    params["body"] = JSON.stringify(body);
  }
  const response = await fetch(`/api/${url}`, params);

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return response.json();
}

async function enrichRecords(
  records: HGTimeRecord[],
  secondsRange: [bigint, bigint],
) {
  const enrichedRecords: HGTimeRecordPlus[] = [];
  let noIntersection = 0;
  for (const baseRecord of records) {
    const record = baseRecord as HGTimeRecordPlus;
    // safeName, not getName: getName throws for interval types it has no
    // format for, and one such record used to take the whole page load with it.
    record.intervalName = safeName(record.interval);
    if (
      record.end_interval &&
      !intervalsAreEqual(record.interval, record.end_interval)
    ) {
      record.endIntervalName = safeName(record.end_interval);
    }

    record.intersection = calculateIntersection(secondsRange, [
      record.start_second,
      record.end_second,
    ]);

    if (record.intersection.intersectionType == HGIntersectionType.None) {
      noIntersection += 1;
    }

    enrichedRecords.push(record);
  }

  // One line per response rather than one per record: a server that starts
  // returning out-of-range rows used to cost thousands of console writes,
  // which on its own is hundreds of milliseconds with devtools open.
  if (noIntersection > 0) {
    console.warn(
      `${noIntersection} of ${records.length} records do not intersect the requested range`,
      secondsRange,
    );
  }

  return enrichedRecords;
}

export async function countRecordsByInterval(
  interval: HGInterval,
  endInterval: HGInterval | null,
) {
  return await apiRequest("records/by-ival/count", "POST", {
    interval: constructIntervalForAPI(interval),
    end_interval: endInterval && constructIntervalForAPI(endInterval),
  });
}

export async function getRecordsByInterval(
  interval: HGInterval,
  endInterval: HGInterval | null,
  secondsRange: [bigint, bigint],
  count: number,
  userID?: string,
  collectionID?: string,
  signal?: AbortSignal,
) {
  const urlParams = new URLSearchParams({});
  urlParams.append("limit", count.toString());

  if (userID) {
    urlParams.append("user_id", userID);
  }
  if (collectionID) {
    urlParams.append("collection_id", collectionID);
  }
  return await enrichRecords(
    expandRecordsMap(
      await apiRequest(
        "records/by-ival?" + urlParams,
        "POST",
        {
          interval: constructIntervalForAPI(interval),
          end_interval: endInterval && constructIntervalForAPI(endInterval),
        },
        signal,
      ),
    ),
    secondsRange,
  );
}

export async function getRecordsByIDs(recordIDs: number[]) {
  const records = expandRecordsMap(
    await apiRequest("records/by-ids", "POST", {
      filter: { ids_whitelist: recordIDs },
    }),
  );
  return records;
}

export async function createRecord(record: HGAnyRecordData): Promise<string> {
  const recordBody = Object.assign({}, record) as unknown as Record<string, unknown>;
  if ("interval" in record) {
    recordBody["interval"] = constructIntervalForAPI(record.interval);
    if (record.end_interval) {
      recordBody["end_interval"] = constructIntervalForAPI(record.end_interval);
    }
  }
  const response = await apiRequest("records", "POST", recordBody);
  store.dispatch(hgApi.util.invalidateTags(["HGDBCounts", "HGRecord"]));
  return response.id as string;
}

export async function updateRecord(recordID: string, record: HGAnyRecordData) {
  const recordBody = Object.assign({}, record) as unknown as Record<string, unknown>;
  if ("interval" in record) {
    recordBody["interval"] = constructIntervalForAPI(record.interval);
    if (record.end_interval) {
      recordBody["end_interval"] = constructIntervalForAPI(record.end_interval);
    }
  }
  await apiRequest(`records/${recordID}`, "PUT", recordBody);
  store.dispatch(hgApi.util.invalidateTags(["HGRecord", "HGDBCounts"]));
}

export async function deleteRecord(recordID: string) {
  await apiRequest(`records/${recordID}`, "DELETE");
  store.dispatch(hgApi.util.invalidateTags(["HGRecord", "HGDBCounts"]));
}
